import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import type { TablePaginationConfig } from 'antd/lib/table/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { Routes } from '@share/constants';
import { RootState } from '@share/utils';
import { IUserWalletRewardItem, RewardWalletPriority } from '@share/common-types';
import { InputFilterType } from '@share/common-types';
import { getUserWalletReward } from '@share/store/slices';
import { SearchTable } from '@components';
import { IColumnMenu, orderSort } from '@constants';
import moment from 'moment';

import { RewardBookingType } from './reward-booking-type';
import { RewardStatus } from './reward-status';
import { RewardInformation } from './reward-information';
import { RewardExpirationCell } from './reward-expiration-cell';
import { RewardsSummary } from './rewards-summary';
import { RewardsNextAvailable } from './rewards-next-available';
import { RewardsProgressBar } from './rewards-progress-bar';
import { RewardsLifetimeValue } from './rewards-lifetime-value';
import { RewardsTierBadge } from './rewards-tier-badge';

import './style.scss';
import { getNextRewardByPriority } from '@utils';

const DEFAULT_MEDIA_POINT = 768;

export type RewardsTabKey = 'available' | 'redeemed' | 'expired' | 'all';

const TAB_KEYS: RewardsTabKey[] = ['available', 'redeemed', 'expired', 'all'];

const STATUS_MAP: Record<RewardsTabKey, string | null> = {
  available: 'Available',
  redeemed: 'Redeemed',
  expired: 'Expired',
  all: null,
};

function formatRewardDate(dateStr: any) {
  if (!dateStr) return '—';
  const m = moment(dateStr, moment.ISO_8601, true);


  return m.isValid() ? m.format('MM/DD/YYYY') : '—';
}

function formatRewardValue(value: number | undefined, account: { walletNoDecimals?: boolean } | null | undefined): string {
  if (value == null) return '—';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: account?.walletNoDecimals ? 0 : 2, maximumFractionDigits: account?.walletNoDecimals ? 0 : 2 })}`;
}

function formatRewardAmountUsed(value: number | undefined, account: { walletNoDecimals?: boolean } | null | undefined): string {
  if (value == null) return '$0.00';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: account?.walletNoDecimals ? 0 : 2, maximumFractionDigits: account?.walletNoDecimals ? 0 : 2 })}`;
}

export function Rewards() {
  const dispatch = useDispatch();

  const intl = useIntl();

  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { userWalletReward, account, user, userWallet } = loginStore;
  
  const currentUser = userWallet ?? user;
  const allRewards = userWalletReward?.allRewards ?? [];

  const [activeTab, setActiveTab] = useState<RewardsTabKey>('available');
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 10 });

  const isMobile = typeof document !== 'undefined' && document.body.offsetWidth <= DEFAULT_MEDIA_POINT;

  const filteredData = useMemo(() => {
    const status = STATUS_MAP[activeTab];
    if (!status) return allRewards;
    return allRewards.filter((r: IUserWalletRewardItem) => (r.status ?? '').toLowerCase() === status.toLowerCase());
  }, [allRewards, activeTab]);

  const allColumns = useMemo((): IColumnMenu[] => {
    const base: IColumnMenu[] = [
      {
        description: intl.formatMessage({ id: 'rewards.column.earnedDate' }),
        key: 'earnedDate',
        width: 130,
        display: true,
        order: 1,
        type: InputFilterType.Custom,
        sortable: true,
        component: (val: string | boolean | number) => <>{formatRewardDate(val as string)}</>,
      },
      {
        description: intl.formatMessage({ id: 'rewards.column.expirationDate' }),
        key: 'expirationDate',
        width: 140,
        display: true,
        order: 2,
        type: InputFilterType.Custom,
        sortable: true,
        component: (_val: string | boolean | number, record: IUserWalletRewardItem) => (
          <RewardExpirationCell
            dateStr={record?.expirationDate}
            daysUntilExpiration={record?.daysUntilExpiration}
            status={record?.status}
          />
        ),
      },
      {
        description: intl.formatMessage({ id: 'rewards.column.rewardValue' }),
        key: 'rewardValue',
        width: 120,
        display: true,
        order: 3,
        type: InputFilterType.Custom,
        sortable: true,
        component: (val: string | boolean | number, record: IUserWalletRewardItem) => (
          <div className="rewards-value-cell">
            <span>{formatRewardValue(Number(val), account)}</span>
            {activeTab === 'redeemed' ? (
              <span className="rewards-value-cell__redeemed">
                {formatRewardAmountUsed(record?.rewardAmountUsed, account)} redeemed
              </span>
            ) : null}
          </div>
        ),
      },
    ];

    if (activeTab === 'redeemed') {
      return [
        {
          description: intl.formatMessage({ id: 'rewards.column.bookingType' }),
          key: 'bookingType',
          width: 120,
          display: true,
          order: 0,
          type: InputFilterType.Custom,
          sortable: true,
          component: (val: string | boolean | number) => <RewardBookingType type={String(val)} />,
        },
        {
          description: intl.formatMessage({ id: 'rewards.column.redeemDate' }),
          key: 'reedemDate',
          width: 130,
          display: true,
          order: 0.5,
          type: InputFilterType.Custom,
          sortable: true,
          component: (val: string | boolean | number) => <>{formatRewardDate(val as string)}</>,
        },
        ...base,
      ].sort(orderSort);
    }

    if (activeTab === 'all') {
      return [
        ...base,
        {
          description: intl.formatMessage({ id: 'rewards.column.status' }),
          key: 'status',
          width: 120,
          display: true,
          sortable: true,
          order: 4,
          type: InputFilterType.Custom,
          component: (val: string | boolean | number) => <RewardStatus status={String(val)} />,
        },
      ].sort(orderSort);
    }

    return base.sort(orderSort);
  }, [activeTab, account, intl]);

  const mobileColumns = useMemo(
    (): IColumnMenu[] => [
      {
        description: intl.formatMessage({ id: 'rewards.column.information' }),
        key: 'information',
        width: 140,
        display: true,
        order: 1,
        type: InputFilterType.Custom,
        component: (_val: string | boolean | number, record: IUserWalletRewardItem) => (
          <RewardInformation
            reward={record}
            account={account}
            displayBookingType={activeTab === 'redeemed'}
            displayRedeemDate={activeTab === 'redeemed'}
            displayStatus={activeTab === 'all'}
          />
        ),
      },
    ],
    [activeTab, account, intl]
  );

  const columns = isMobile ? mobileColumns : allColumns;

  const redeemedCount = allRewards.filter((r: IUserWalletRewardItem) => (r.status ?? '').toLowerCase() === 'redeemed').length;

  const handleRefresh = () => {
    if (currentUser) dispatch(getUserWalletReward(currentUser));
  };

  const handleTableChange = (_pagination: TablePaginationConfig) => {
    if (_pagination) setPagination(_pagination);
  };

  const handleTabChange = (key: RewardsTabKey) => {
    setActiveTab(key);
    setPagination((p) => ({ ...p, current: 1 }));
  };
  
  const actions = useMemo(
    () => [
      {
        renderAction: () => (
          <>
            <div className="container-action">
              <div className="button-action">
                {TAB_KEYS.map((key) => (
                  <Button
                    ghost
                    key={key}
                    className={`button-filter button-effect ${activeTab === key ? 'active' : ''}`}
                    id={key}
                    onClick={() => handleTabChange(key)}
                  >
                    <span><FormattedMessage id={`rewards.tab.${key}`} /></span>
                  </Button>
                ))}
              </div>
              <div className="text-search desktop" style={{ height: '32px' }}>&nbsp;</div>
            </div>
            <div className="button-base-line" />
          </>
        ),
      },
    ],
    [activeTab],
  );

  const showNextAvailable = account?.rewardWalletPriority !== RewardWalletPriority.Best;
  const nextReward = showNextAvailable
    ? getNextRewardByPriority(allRewards, account?.rewardWalletPriority, userWalletReward?.nextAvailableReward)
    : undefined;
  const styleProgressContainer = !nextReward ? { marginTop: '10px' } : {};

  return (
    <div className="reservations-wrapper-container">
      <div className="reservations-wrapper rewards-wrapper rewards-wrapper--reservations-style">
        <div className="rewards-page-top">
          <div className="rewards-page-top__left">
            <RewardsSummary wallet={userWalletReward} />
            <div className="rewards-page-top__progress" style={styleProgressContainer}>
              <RewardsProgressBar wallet={userWalletReward} />
            </div>
          </div>
          {nextReward && (
            <div className="rewards-page-top__right">
              <RewardsNextAvailable
                allRewards={allRewards}
                nextAvailableReward={userWalletReward?.nextAvailableReward}
                priority={account?.rewardWalletPriority}
                account={account}
              />
            </div>)}
      </div>

        <div className="rewards-page-secondary">
          <RewardsLifetimeValue allRewards={allRewards} currency={userWalletReward?.currency} />
          <RewardsTierBadge rewardsEarnedCount={redeemedCount} />
        </div>

        <div className="rewards-activity-section">
          <h2 className="rewards-activity-section__title">
            <FormattedMessage id="rewards.activityTitle" />
          </h2>
          {allRewards.length === 0 ? (
            <div className="rewards-empty-state">
              <p className="rewards-empty-state__title">
                <FormattedMessage id="rewards.empty.title" />
              </p>
              <p className="rewards-empty-state__subtitle">
                <FormattedMessage id="rewards.empty.subtitle" />
              </p>
            </div>
          ) : (
            <SearchTable
              key={`rewards-${activeTab}`}
              title={''}
              loading={false}
              actions={actions}
              allColumns={columns}
              dataSource={filteredData}
              pagination={pagination}
              filters={undefined}
              totalOrderCount={filteredData.length}
              rowKey={(row: IUserWalletRewardItem) => String(row.rewardId ?? Math.random())}
              isFilterApplied={false}
              onFiltersChange={() => {}}
              onTableChange={handleTableChange}
              onRowClick={() => {}}
              onResetFilters={() => {}}
              onApplyFilters={() => {}}
              onRefresh={handleRefresh}
            ></SearchTable>
          )}
        </div>

        <div className="rewards-learn-more">
          <h2 className="rewards-learn-more__title">
            <FormattedMessage id="rewards.learnMore.title" />
          </h2>
          <div className="rewards-learn-more__list">
            {account?.name ? (
              <Link
                to={`/${account.name}${Routes.RewardsLearn}`}
                className="rewards-learn-more__item rewards-learn-more__item--link"
              >
                <span className="rewards-learn-more__item-text">
                  <FormattedMessage id="rewards.learnMore.membersGetMore" />
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="rewards-learn-more__item-chevron" />
              </Link>
            ) : (
              <div className="rewards-learn-more__item">
                <span className="rewards-learn-more__item-text">
                  <FormattedMessage id="rewards.learnMore.membersGetMore" />
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="rewards-learn-more__item-chevron" />
              </div>
            )}
            {account?.name ? (
              <Link
                to={`/${account.name}${Routes.RewardsQa}`}
                className="rewards-learn-more__item rewards-learn-more__item--link"
              >
                <span className="rewards-learn-more__item-text">
                  <FormattedMessage id="rewards.learnMore.faq" />
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="rewards-learn-more__item-chevron" />
              </Link>
            ) : (
              <div className="rewards-learn-more__item">
                <span className="rewards-learn-more__item-text">
                  <FormattedMessage id="rewards.learnMore.faq" />
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="rewards-learn-more__item-chevron" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
